import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Textarea } from '../components/Textarea';
import { Select } from '../components/Select';

export const SendResponsePage = () => {
  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const responseTypeParam = urlParams.get('type') || 'pass';
  const requestTypeParam = urlParams.get('request') || 'partial';
  const queryId = window.location.pathname.match(/\/agent\/queries\/(\d+)\/respond/)?.[1];

  const [responseType, setResponseType] = useState<'pass' | 'request'>(responseTypeParam as 'pass' | 'request');
  const [requestType, setRequestType] = useState<'partial' | 'full'>(requestTypeParam as 'partial' | 'full');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [deadline, setDeadline] = useState('30');
  const [showPreview, setShowPreview] = useState(false);

  // Mock data
  const query = {
    id: queryId,
    author: 'Emma Rodriguez',
    title: 'The Silent Shore',
    genre: 'Literary Fiction',
  };

  const passTemplates = [
    {
      value: 'professional',
      label: 'Professional Pass',
      content: `Dear [Author Name],

Thank you for querying me with [TITLE]. I appreciate you thinking of me for representation.

After careful consideration, I've decided that this project isn't the right fit for my list at this time. The publishing marketplace is incredibly subjective, and I encourage you to continue querying other agents who may feel differently.

I wish you the very best in your publishing journey.

Best regards,
[Your Name]`,
    },
    {
      value: 'encouraging',
      label: 'Encouraging Pass',
      content: `Dear [Author Name],

Thank you so much for sharing [TITLE] with me. I really enjoyed [specific element], and I think your writing shows [strength].

Unfortunately, I don't feel I'm the right agent to take this project forward. [Brief reason - market saturation, not connecting with the voice, etc.]. However, I believe this project has potential and encourage you to continue submitting.

I wish you all the best with your writing career.

Warm regards,
[Your Name]`,
    },
    {
      value: 'brief',
      label: 'Brief Pass',
      content: `Dear [Author Name],

Thank you for your query. Unfortunately, I'm going to pass on representing [TITLE]. I wish you the best of luck finding the right agent.

Best,
[Your Name]`,
    },
  ];

  const requestTemplates = [
    {
      value: 'partial',
      label: 'Request Partial - Professional',
      content: `Dear [Author Name],

Thank you for your query for [TITLE]. I'm intrigued by your premise and would like to read more.

Please send me the first [pages/chapters] of your manuscript at your earliest convenience. I aim to respond within [timeframe].

I look forward to reading your work.

Best regards,
[Your Name]`,
    },
    {
      value: 'full',
      label: 'Request Full - Enthusiastic',
      content: `Dear [Author Name],

I was completely drawn in by your query for [TITLE]! [Specific element] really resonated with me, and I'm eager to read the complete manuscript.

Please send the full manuscript at your earliest convenience. I'm very excited to dive in and will aim to get back to you within [timeframe].

Thank you for thinking of me for representation.

Best regards,
[Your Name]`,
    },
    {
      value: 'full_professional',
      label: 'Request Full - Professional',
      content: `Dear [Author Name],

Thank you for querying me with [TITLE]. Based on your query and sample pages, I'd like to review the complete manuscript.

Please send the full manuscript when convenient. I'll do my best to respond within [timeframe].

Looking forward to reading.

Best,
[Your Name]`,
    },
  ];

  const templates = responseType === 'pass' ? passTemplates : requestTemplates;

  const handleTemplateChange = (templateValue: string) => {
    setSelectedTemplate(templateValue);
    const template = templates.find(t => t.value === templateValue);
    if (template) {
      let content = template.content;
      content = content.replace(/\[Author Name\]/g, query.author);
      content = content.replace(/\[TITLE\]/g, query.title.toUpperCase());
      content = content.replace(/\[Your Name\]/g, 'Sarah Johnson');

      if (responseType === 'request') {
        content = content.replace(/\[pages\/chapters\]/g, requestType === 'partial' ? '50 pages' : 'the full manuscript');
        content = content.replace(/\[timeframe\]/g, `${deadline} days`);
      }

      setCustomMessage(content);
    }
  };

  const handleSend = () => {
    if (!customMessage.trim()) {
      window.alert('Please write a response message');
      return;
    }

    console.log('Sending response:', {
      queryId,
      responseType,
      requestType: responseType === 'request' ? requestType : null,
      deadline: responseType === 'request' ? deadline : null,
      message: customMessage,
    });

    window.alert(`Response sent successfully to ${query.author}!`);
    window.location.href = '/agent/queries';
  };

  if (showPreview) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        fontFamily: theme.fonts.primary,
      }}>
        <Header />
        <main style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: '60px 40px',
        }}>
          <div style={{ marginBottom: '32px' }}>
            <h1 style={{
              fontSize: '40px',
              fontWeight: '400',
              marginBottom: '8px',
              color: theme.colors.textPrimary,
            }}>
              Preview Response
            </h1>
            <p style={{
              fontSize: '18px',
              color: theme.colors.textSecondary,
            }}>
              Review your message before sending
            </p>
          </div>

          <Card style={{ marginBottom: '24px' }}>
            <div style={{
              padding: '16px',
              backgroundColor: theme.colors.backgroundAlt,
              borderRadius: theme.borderRadius.medium,
              marginBottom: '16px',
            }}>
              <div style={{
                fontSize: '14px',
                color: theme.colors.textSecondary,
                marginBottom: '8px',
              }}>
                <strong>To:</strong> {query.author}
              </div>
              <div style={{
                fontSize: '14px',
                color: theme.colors.textSecondary,
                marginBottom: '8px',
              }}>
                <strong>Re:</strong> {query.title}
              </div>
              <div style={{
                fontSize: '14px',
                color: theme.colors.textSecondary,
              }}>
                <strong>Response Type:</strong> {responseType === 'pass' ? 'Pass' : `Request ${requestType === 'partial' ? 'Partial' : 'Full'} Manuscript`}
              </div>
            </div>

            <div style={{
              padding: '20px',
              backgroundColor: theme.colors.white,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.medium,
              fontSize: '15px',
              lineHeight: '1.8',
              color: theme.colors.textPrimary,
              whiteSpace: 'pre-wrap',
            }}>
              {customMessage}
            </div>

            {responseType === 'request' && (
              <div style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: theme.colors.successLight,
                borderRadius: theme.borderRadius.small,
                fontSize: '14px',
                color: theme.colors.textPrimary,
              }}>
                ✓ Requested deadline: {deadline} days from now
              </div>
            )}
          </Card>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '16px',
          }}>
            <Button
              variant="ghost"
              onClick={() => setShowPreview(false)}
            >
              ← Back to Edit
            </Button>
            <Button onClick={handleSend}>
              Send Response
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Send Response
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            Respond to query from {query.author}
          </p>
        </div>

        {/* Query Info */}
        <Card style={{ marginBottom: '32px', backgroundColor: theme.colors.backgroundAlt }}>
          <div style={{
            fontSize: '14px',
            color: theme.colors.textSecondary,
          }}>
            <div style={{ marginBottom: '4px' }}>
              <strong>Manuscript:</strong> {query.title}
            </div>
            <div style={{ marginBottom: '4px' }}>
              <strong>Author:</strong> {query.author}
            </div>
            <div>
              <strong>Genre:</strong> {query.genre}
            </div>
          </div>
        </Card>

        {/* Response Type */}
        <Card style={{ marginBottom: '24px' }}>
          <label style={{
            fontSize: '16px',
            fontWeight: '500',
            color: theme.colors.textPrimary,
            marginBottom: '12px',
            display: 'block',
          }}>
            Response Type *
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '16px',
          }}>
            <button
              onClick={() => setResponseType('pass')}
              style={{
                padding: '20px',
                border: `2px solid ${responseType === 'pass' ? theme.colors.error : theme.colors.border}`,
                borderRadius: theme.borderRadius.medium,
                backgroundColor: responseType === 'pass' ? theme.colors.errorLight : theme.colors.white,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <div style={{
                fontSize: '16px',
                fontWeight: '500',
                color: theme.colors.textPrimary,
              }}>
                Pass
              </div>
            </button>

            <button
              onClick={() => setResponseType('request')}
              style={{
                padding: '20px',
                border: `2px solid ${responseType === 'request' ? theme.colors.success : theme.colors.border}`,
                borderRadius: theme.borderRadius.medium,
                backgroundColor: responseType === 'request' ? theme.colors.successLight : theme.colors.white,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <div style={{
                fontSize: '16px',
                fontWeight: '500',
                color: theme.colors.textPrimary,
              }}>
                Request Manuscript
              </div>
            </button>
          </div>
        </Card>

        {/* Request Type (if requesting) */}
        {responseType === 'request' && (
          <Card style={{ marginBottom: '24px' }}>
            <label style={{
              fontSize: '16px',
              fontWeight: '500',
              color: theme.colors.textPrimary,
              marginBottom: '12px',
              display: 'block',
            }}>
              Request Type *
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '16px',
            }}>
              <button
                onClick={() => setRequestType('partial')}
                style={{
                  padding: '20px',
                  border: `2px solid ${requestType === 'partial' ? theme.colors.primary : theme.colors.border}`,
                  borderRadius: theme.borderRadius.medium,
                  backgroundColor: requestType === 'partial' ? theme.colors.primaryLight : theme.colors.white,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{
                  fontSize: '16px',
                  fontWeight: '500',
                  color: theme.colors.textPrimary,
                  marginBottom: '4px',
                }}>
                  Partial
                </div>
                <div style={{
                  fontSize: '13px',
                  color: theme.colors.textSecondary,
                }}>
                  First 50 pages
                </div>
              </button>

              <button
                onClick={() => setRequestType('full')}
                style={{
                  padding: '20px',
                  border: `2px solid ${requestType === 'full' ? theme.colors.primary : theme.colors.border}`,
                  borderRadius: theme.borderRadius.medium,
                  backgroundColor: requestType === 'full' ? theme.colors.primaryLight : theme.colors.white,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{
                  fontSize: '16px',
                  fontWeight: '500',
                  color: theme.colors.textPrimary,
                  marginBottom: '4px',
                }}>
                  Full Manuscript
                </div>
                <div style={{
                  fontSize: '13px',
                  color: theme.colors.textSecondary,
                }}>
                  Complete work
                </div>
              </button>
            </div>

            <div style={{ marginTop: '24px' }}>
              <Select
                label="Response Deadline"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                options={[
                  { value: '14', label: '14 days (2 weeks)' },
                  { value: '30', label: '30 days (1 month)' },
                  { value: '45', label: '45 days (6 weeks)' },
                  { value: '60', label: '60 days (2 months)' },
                  { value: '90', label: '90 days (3 months)' },
                ]}
                helperText="How long you expect to take reviewing the manuscript"
              />
            </div>
          </Card>
        )}

        {/* Template Selection */}
        <Card style={{ marginBottom: '24px' }}>
          <Select
            label="Response Template (Optional)"
            value={selectedTemplate}
            onChange={(e) => handleTemplateChange(e.target.value)}
            options={[
              { value: '', label: 'Start from scratch...' },
              ...templates.map(t => ({ value: t.value, label: t.label })),
            ]}
            helperText="Choose a template to get started, then customize as needed"
          />
        </Card>

        {/* Custom Message */}
        <Card style={{ marginBottom: '32px' }}>
          <Textarea
            label="Your Message *"
            placeholder="Write your response to the author..."
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            rows={12}
            required
            helperText="Be professional and respectful in all communications"
          />
        </Card>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '16px',
        }}>
          <Button
            variant="ghost"
            onClick={() => window.location.href = `/agent/queries/${queryId}`}
          >
            ← Cancel
          </Button>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button
              variant="outline"
              onClick={() => setShowPreview(true)}
              disabled={!customMessage.trim()}
            >
              Preview
            </Button>
            <Button
              onClick={handleSend}
              disabled={!customMessage.trim()}
            >
              Send Response
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
